import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
// ─────────────────────────────────────────────────────────────────────────────
import { Dialog } from '../../interfaces/create-dialog.interface';
import {
  AddProjectDialogData,
  AddProjectDialogResult,
  ChunkCopyToDialogData,
  ChunkCopyToDialogResult,
  ChunkItemInfoDialogData,
  ConfirmDialogResult,
  CreateDialogResult,
  DialogResult,
  EditProjectDialogData,
  EditProjectDialogResult,
  FileInfoDialogData,
  GlobalVarDialogData,
  GlobalVarDialogResult,
  InputDialogData,
  InputDialogResult,
  ManageTeamDialogData,
  UrlDialogData,
  UrlDialogResult,
  VersionDialogData,
  WatchFileDialogData,
} from '../../interfaces/dialog-types.interface';
// ─────────────────────────────────────────────────────────────────────────────
// Shared dialogs from shared/modules/ (architecture-compliant)
// ─────────────────────────────────────────────────────────────────────────────
// Create dialog
import { ConfirmDialogComponent as SharedConfirmDialogComponent } from '../../modules/confirm-dialog/confirm-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Create dialog
import { CreateDialogComponent } from '../../modules/create-dialog/create-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Create var dialog
import { GlobalVarDialogComponent } from '../../modules/global-var-dialog/global-var-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Create file from url
import { UrlDialogComponent } from '../../modules/url-dialog/url-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Watch file dialog
import { WatchFileDialogComponent } from '../../modules/watch-file-dialog/watch-file-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Chunk copy to notebook dialog
import { ChunkCopyToDialogComponent } from '../../modules/chunk-copy-to-dialog/chunk-copy-to-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Database controls dialog
// ─────────────────────────────────────────────────────────────────────────────
// Add project dialog
import { AddProjectDialogComponent } from '../../modules/add-project-dialog/add-project-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Edit project dialog
import { EditProjectDialogComponent } from '../../modules/edit-project-dialog/edit-project-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Manage team dialog
import { ManageTeamDialogComponent } from '../../modules/manage-team-dialog/manage-team-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Version dialog (layout - kept here for backward compatibility)
import { VersionDialogComponent } from '../../../layout/project-header/version-dialog/version-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
import { ChunkDialogComponent } from '../../modules/chunk-dialog/chunk-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Schema values dialog (collects variable_schema input for editor runs)
import {
  SchemaValuesDialogComponent,
  SchemaValuesDialogData,
  SchemaValuesDialogResult,
} from '../../modules/schema-values-dialog/schema-values-dialog.component';
// Chunk item description edit
import { ChunkItemInfoComponent } from '../../modules/chunk-item-description/chunk-item-description.component';
// ─────────────────────────────────────────────────────────────────────────────
// Import project dialog
import { ImportProjectDialogComponent } from '../../modules/import-project-dialog/import-project-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Input dialog
import { InputDialogComponent } from '../../modules/input-dialog/input-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// File info dialog
import { FileInfoDialogComponent } from '../../modules/file-info/file-info-dialog/file-info-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Feature dialogs service (for backward compatibility delegation)
import { FeatureDialogService } from '../../../components/feature-dialog.service';
import { ConnectionTestingDialogData } from '../../../components/connection-testing-dialog/connection-testing-dialog.component';
import { RemoveTeamMemberDialogData } from '../../../components/remove-team-member-dialog/remove-team-member-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Constants
import { CLOSE_URL_DIALOG } from '../../constants/general.constants';
// ─────────────────────────────────────────────────────────────────────────────
// Message service
import { MessageService } from '../message/message.service';

// Unified confirm options to keep API stable and flexible
export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmCaption?: string;
  cancelCaption?: string;
  isDestructive?: boolean;
  width?: string;
  // Use legacy shared ConfirmDialog (modules) for advanced layouts
  useLegacySharedDialog?: boolean;
  // Pass-through for advanced shared dialog content (use Dialog for legacy shared dialog)
  dialogData?: Dialog;
  hasClose?: boolean;
}

/**
 * DialogService - Manages shared/modules dialogs
 * 
 * This service handles dialogs from the shared/modules/ directory only,
 * maintaining proper architecture boundaries.
 * 
 * For feature dialogs (components/), methods delegate to FeatureDialogService
 * for backward compatibility. New code should use FeatureDialogService directly.
 * 
 * Architecture:
 * - Shared dialogs (shared/modules/) → DialogService (shared/services/)
 * - Feature dialogs (components/) → FeatureDialogService (components/)
 * - Layout dialogs (layout/) → Handled here for backward compatibility
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);

  // Shared dialog refs (shared/modules/) - typed for better type safety
  private createDialogRef!: MatDialogRef<CreateDialogComponent, CreateDialogResult>;
  private confirmDialogRef!: MatDialogRef<SharedConfirmDialogComponent, ConfirmDialogResult>;
  private createVarDialogRef!: MatDialogRef<GlobalVarDialogComponent, GlobalVarDialogResult>;
  private urlDialogRef!: MatDialogRef<UrlDialogComponent, UrlDialogResult>;
  private watchFileDialogRef!: MatDialogRef<WatchFileDialogComponent, void>;
  private chunkCopyToDialogRef!: MatDialogRef<ChunkCopyToDialogComponent, ChunkCopyToDialogResult>;
  private addProjectDialogRef!: MatDialogRef<AddProjectDialogComponent, AddProjectDialogResult>;
  private editProjectDialogRef!: MatDialogRef<EditProjectDialogComponent, EditProjectDialogResult>;
  private manageTeamDialogRef!: MatDialogRef<ManageTeamDialogComponent, void>;
  private versionDialogRef!: MatDialogRef<VersionDialogComponent, void>;
  private chunkDialogRef!: MatDialogRef<ChunkDialogComponent, Dialog>;
  private chunkItemInfoDialogRef!: MatDialogRef<ChunkItemInfoComponent, ChunkItemInfoDialogData>;
  private importProjectDialogRef!: MatDialogRef<ImportProjectDialogComponent, Dialog>;
  private inputDialogRef!: MatDialogRef<InputDialogComponent, InputDialogResult>;
  private fileInfoDialogComponentRef!: MatDialogRef<FileInfoDialogComponent, void>;

  // Message subscription
  private messageSubscription!: Subscription;

  // Feature dialog service for backward compatibility delegation
  private readonly featureDialogService = inject(FeatureDialogService);

  constructor() {
    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message && message.text === CLOSE_URL_DIALOG) {
          this.urlDialogRef.close(message.data as UrlDialogResult);
        }
      });
  }

  createDialog(data: Dialog): Observable<DialogResult<CreateDialogResult>> {
    this.createDialogRef = this.dialog.open(CreateDialogComponent, {
      data,
      width: '400px',
      disableClose: true,
    });
    return this.createDialogRef.afterClosed();
  }

  confirmDialog(data: Dialog): Observable<DialogResult<ConfirmDialogResult>> {
    this.confirmDialogRef = this.dialog.open(SharedConfirmDialogComponent, {
      data,
      width: '400px',
      disableClose: true,
    });
    return this.confirmDialogRef.afterClosed();
  }

  /**
   * Unified confirmation API. Defaults to simple components confirm dialog,
   * optionally routes to the legacy shared dialog for advanced content.
   */
  confirm(options: ConfirmOptions): Observable<boolean> {
    const width = options.width || '400px';

    if (options.useLegacySharedDialog) {
      // Map to legacy Dialog interface for shared/modules confirm dialog
      const legacyData: Dialog = {
        title: options.title,
        message: options.message,
        confirmCaption: options.confirmCaption || 'Confirm',
        cancelCaption: options.cancelCaption || 'Cancel',
        dialogData: options.dialogData,
        hasClose: options.hasClose,
      };

      const ref = this.dialog.open(SharedConfirmDialogComponent, {
        data: legacyData,
        width,
        disableClose: true,
      });
      return ref.afterClosed();
    }

    // Delegate to FeatureDialogService for components confirm dialog
    return this.featureDialogService.confirmDialog({
      title: options.title,
      message: options.message,
      confirmButtonText: options.confirmCaption,
      cancelButtonText: options.cancelCaption,
      isDestructive: options.isDestructive,
    });
  }

  createVarDialog(data: GlobalVarDialogData): Observable<DialogResult<GlobalVarDialogResult>> {
    this.createVarDialogRef = this.dialog.open(GlobalVarDialogComponent, {
      data,
      width: '600px',
      disableClose: true,
    });
    return this.createVarDialogRef.afterClosed();
  }

  createFileFromUrlDialog(data: UrlDialogData): Observable<DialogResult<UrlDialogResult>> {
    this.urlDialogRef = this.dialog.open(UrlDialogComponent, {
      data,
      width: '600px',
      disableClose: true,
    });
    return this.urlDialogRef.afterClosed();
  }

  getInfoFileDialog(data: FileInfoDialogData): Observable<DialogResult<void>> {
    this.fileInfoDialogComponentRef = this.dialog.open(FileInfoDialogComponent, {
      data,
      width: '600px',
      disableClose: true,
    });
    return this.fileInfoDialogComponentRef.afterClosed();
  }

  watchFileDialog(data: WatchFileDialogData): Observable<DialogResult<void>> {
    this.watchFileDialogRef = this.dialog.open(WatchFileDialogComponent, {
      data,
      width: '1024px',
      disableClose: true,
    });
    return this.watchFileDialogRef.afterClosed();
  }

  chunkCopyToDialog(data: ChunkCopyToDialogData): Observable<DialogResult<ChunkCopyToDialogResult>> {
    this.chunkCopyToDialogRef = this.dialog.open(ChunkCopyToDialogComponent, {
      data,
      width: '480px',
      disableClose: true,
    });
    return this.chunkCopyToDialogRef.afterClosed();
  }

  addProjectDialog(data: AddProjectDialogData): Observable<DialogResult<AddProjectDialogResult>> {
    this.addProjectDialogRef = this.dialog.open(AddProjectDialogComponent, {
      data,
      width: '768px',
      disableClose: true,
    });
    return this.addProjectDialogRef.afterClosed();
  }

  editProjectDialog(data: EditProjectDialogData): Observable<DialogResult<EditProjectDialogResult>> {
    this.editProjectDialogRef = this.dialog.open(EditProjectDialogComponent, {
      data,
      width: '400px',
      disableClose: true,
    });
    return this.editProjectDialogRef.afterClosed();
  }

  manageTeamDialog(data: ManageTeamDialogData): Observable<DialogResult<void>> {
    this.manageTeamDialogRef = this.dialog.open(ManageTeamDialogComponent, {
      data,
      width: '800px',
      disableClose: true,
    });
    return this.manageTeamDialogRef.afterClosed();
  }

  versionDialog(data: VersionDialogData): Observable<DialogResult<void>> {
    this.versionDialogRef = this.dialog.open(VersionDialogComponent, {
      data,
      width: '400px',
      disableClose: true,
    });
    return this.versionDialogRef.afterClosed();
  }

  chunkDialog(data: Dialog): Observable<DialogResult<Dialog>> {
    this.chunkDialogRef = this.dialog.open(ChunkDialogComponent, {
      data,
      width: '1024px',
      disableClose: true,
    });
    return this.chunkDialogRef.afterClosed();
  }

  /**
   * Open the generic variable_schema input dialog and return the collected
   * values (SCRUM-690). Resolves to undefined/null when the operator cancels.
   */
  schemaValuesDialog(
    data: SchemaValuesDialogData,
  ): Observable<DialogResult<SchemaValuesDialogResult>> {
    const ref = this.dialog.open(SchemaValuesDialogComponent, {
      data,
      width: '580px',
      disableClose: true,
    });
    return ref.afterClosed();
  }

  chunkItemInfoDialog(data: ChunkItemInfoDialogData): Observable<DialogResult<ChunkItemInfoDialogData>> {
    this.chunkItemInfoDialogRef = this.dialog.open(ChunkItemInfoComponent, {
      data,
      width: '1024px',
      disableClose: true,
    });
    return this.chunkItemInfoDialogRef.afterClosed();
  }

  importProjectDialog(data: Dialog): Observable<DialogResult<Dialog>> {
    this.importProjectDialogRef = this.dialog.open(
      ImportProjectDialogComponent,
      {
        data,
        width: '600px',
        disableClose: true,
      }
    );
    return this.importProjectDialogRef.afterClosed();
  }

  inputDialog(data: InputDialogData): Observable<DialogResult<InputDialogResult>> {
    this.inputDialogRef = this.dialog.open(InputDialogComponent, {
      data,
      width: '400px',
      disableClose: true,
    });
    return this.inputDialogRef.afterClosed();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Feature dialogs - Delegated to FeatureDialogService for backward compatibility
  // New code should use FeatureDialogService directly
  // NOTE: These methods use unknown types as they are deprecated and will be removed
  // ─────────────────────────────────────────────────────────────────────────────

  /** @deprecated Use FeatureDialogService.editRoutineDialog() directly */
  editRoutineDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.editRoutineDialog(data);
  }

  /** @deprecated Use FeatureDialogService.routineStatsModal() directly */
  routineStatsModal(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.routineStatsModal(data);
  }

  /** @deprecated Use FeatureDialogService.workflowCreateDialog() directly */
  workflowCreateDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.workflowCreateDialog(data);
  }

  /** @deprecated Use FeatureDialogService.exportProjectDialog() directly */
  exportProjectDialog(data?: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.exportProjectDialog(data);
  }

  /** @deprecated Use FeatureDialogService.inviteTeamMemberDialog() directly */
  inviteTeamMemberDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.inviteTeamMemberDialog(data);
  }

  /** @deprecated Use FeatureDialogService.removeTeamMemberDialog() directly */
  removeTeamMemberDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.removeTeamMemberDialog(data as RemoveTeamMemberDialogData);
  }

  /** @deprecated Use FeatureDialogService.editRoleDialog() directly */
  editRoleDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.editRoleDialog(data);
  }

  /** @deprecated Use FeatureDialogService.addConnectionDialog() directly */
  addConnectionDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.addConnectionDialog(data);
  }

  connectionSuccessDialog(connectionName: string): Observable<'skip' | 'start-testing'> {
    return this.featureDialogService.connectionSuccessDialog(connectionName);
  }

  connectionTestingDialog(data: ConnectionTestingDialogData): Observable<void> {
    return this.featureDialogService.connectionTestingDialog(data);
  }

  runNowDialog(data: any): Observable<any> {
    return this.featureDialogService.runNowDialog(data);
  }

  /** @deprecated Use FeatureDialogService.addPlanDialog() directly */
  addPlanDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.addPlanDialog(data);
  }

  /** @deprecated Use FeatureDialogService.stepFormDialog() directly */
  stepFormDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.stepFormDialog(data);
  }

  /** @deprecated Use FeatureDialogService.snapshotManagementModal() directly */
  snapshotManagementModal(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.snapshotManagementModal(data);
  }

  /** @deprecated Use FeatureDialogService.secretValueDialog() directly */
  secretValueDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.secretValueDialog(data);
  }

  /** @deprecated Use FeatureDialogService.projectCreateDialog() directly */
  projectCreateDialog(data?: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.projectCreateDialog(data);
  }

  /** @deprecated Use FeatureDialogService.importProjectDialog() directly */
  componentsImportProjectDialog(data?: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.importProjectDialog(data);
  }

  /** @deprecated Use FeatureDialogService.createSnapshotModal() directly */
  createSnapshotModal(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.createSnapshotModal(data);
  }

  /** @deprecated Use FeatureDialogService.editSnapshotModal() directly */
  editSnapshotModal(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.editSnapshotModal(data);
  }

  /** @deprecated Use FeatureDialogService.viewSnapshotDescriptionModal() directly */
  viewSnapshotDescriptionModal(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.viewSnapshotDescriptionModal(data);
  }

  /** @deprecated Use FeatureDialogService.confirmApplySnapshotDialog() directly */
  confirmApplySnapshotDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.confirmApplySnapshotDialog(data);
  }

  /** @deprecated Use FeatureDialogService.saveAndApplySnapshotDialog() directly */
  saveAndApplySnapshotDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.saveAndApplySnapshotDialog(data);
  }

  /** @deprecated Use FeatureDialogService.planHistoryModal() directly */
  planHistoryModal(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.planHistoryModal(data);
  }

  /** @deprecated Use FeatureDialogService.projectSettingsDialog() directly */
  projectSettingsDialog(data: unknown): Observable<DialogResult<unknown>> {
    return this.featureDialogService.projectSettingsDialog(data);
  }
}
